/* eslint-disable max-lines-per-function */
import {
  ColumnDef,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  Row,
  SortDirection,
  SortingState,
  useReactTable,
} from '@tanstack/react-table';
import {
  DropdownEntry,
  DropdownGroup,
  DropdownMenu,
  SkeletonLoader,
  TableCell,
  TableHeaderIcons,
  TanStackTableRoot,
} from '@toggle/design-system';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useNavigateToAssetOverview } from '~/hooks/use-navigate-to-asset-overview/useNavigateToAssetOverview';
import { useQueryParams } from '~/hooks/use-query-params/useQueryParams';
import { TabsType as AOTabsType } from '~/modules/tab-content/types';
import { parseEarningsInsights } from '~/react-query-hooks/insights/useEarningsInsightsFilter.utils';
import { queryKeys } from '~/routes/query-params';
import { getEarningsInsightData } from '~/services/insights/insights-service';
import { POSTHOG_TRACKING_SOURCES } from '~/services/tracking/posthog/constants';
import { usePosthogEvents } from '~/services/tracking/posthog/use-posthog-events/usePosthogEvents';
import { useSubscription } from '~/stores/use-subscription/useSubscription';
import { getCalendarEventType } from '~/views/dashboard/calendar-widget/calendar-date-card/calendar-day-card/utils';
import { getHeaderProps } from '~/views/dashboard/calendar-widget/calendar-event-drawer/calendar-event-drawer-header/utils';
import { CalendarEventDrawer } from '~/views/dashboard/calendar-widget/calendar-event-drawer/CalendarEventDrawer';
import { getDrawerContent } from '~/views/dashboard/calendar-widget/calendar-event-drawer/drawer-content/utils/utils';
import {
  CalendarEvents,
  isCompanyEvent,
  isEarningsEvent,
} from '~/views/dashboard/calendar-widget/calendar-event-drawer/utils';
import {
  SubTabsType,
  useEarningsInsightDrawer,
} from '~/widgets/earnings-insight-drawer/useEarningsInsightDrawer';

import { EmptyState } from '../empty-state/EmptyState';
import { useEventsColumns } from '../useEventsColumns';
import { formatEventDate, TabsType } from '../utils';
import * as S from './EventsTable.styles';
import { EventTableBodySkeletonLoader } from './EventTableBodySkeletonLoader';

export interface TableDataSet {
  data: Record<string, CalendarEvents[]>;
  isLoading: boolean;
  isError: boolean;
  hasNextPage?: boolean;
}

export interface EventsTableProps {
  activeTab: TabsType;
  activeDate: Date;
  tableDataSet: TableDataSet;
  forceIsTableLoading: boolean;
  setForceIsTableLoading: (isLoading: boolean) => void;
  tableContainerRef?: React.RefObject<HTMLDivElement | null>;
}

export const EventsTable = ({
  activeTab,
  activeDate,
  tableDataSet,
  forceIsTableLoading,
  setForceIsTableLoading,
  tableContainerRef,
}: EventsTableProps) => {
  const { t } = useTranslation('common');
  const isBasicOrLightUser = useSubscription(state =>
    state.isBasicOrLightUser()
  );
  const { openDrawer } = useEarningsInsightDrawer();
  const { navigateToPage } = useNavigateToAssetOverview();
  const { trackEventsDrawerOpen } = usePosthogEvents();
  const { setQueryParams, deleteQueryParam } = useQueryParams();

  const [drawerData, setDrawerData] = useState<CalendarEvents | undefined>();
  const [activeSortingState, setActiveSortingState] = useState<SortingState>(
    []
  );

  useEffect(() => {
    const handler = setTimeout(() => {
      setForceIsTableLoading(false);
    }, 20);
    return () => clearTimeout(handler);
  }, [forceIsTableLoading, setForceIsTableLoading]);

  const onFullScreenClick = (data: CalendarEvents) => {
    if (isEarningsEvent(data)) {
      return () =>
        navigateToPage({
          entityTag: data.entity_tag,
          tab: AOTabsType.Earnings,
        });
    } else if (isCompanyEvent(data) && data.entity) {
      return () =>
        navigateToPage({
          entityTag: data.entity_tag,
        });
    }

    return undefined;
  };

  const { data, isError, isLoading, hasNextPage } = tableDataSet;
  const activeDateData = data[formatEventDate(activeDate)] ?? [];
  const columns = useEventsColumns({
    activeTab,
  });

  const getAlignment = (index: number) => {
    switch (activeTab) {
      case TabsType.Earnings:
      case TabsType.Economics:
        return index > 2 ? 'end' : 'start';
      case TabsType.Company:
      default:
        return 'start';
    }
  };

  const getDropdownItems = (
    columnId: string,
    sortOrder: false | SortDirection
  ) => {
    const enabledKeys = sortOrder
      ? ['sortUp', 'sortDown', 'clearSort']
      : ['sortUp', 'sortDown'];

    const items: DropdownEntry[] = [
      {
        label: t('common:table.dropdown_ascending'),
        iconName: 'SortUp',
        key: 'sortUp',
        onClick: () =>
          setActiveSortingState([
            {
              id: columnId,
              desc: false,
            },
          ]),
      },
      {
        label: t('common:table.dropdown_descending'),
        iconName: 'SortDown',
        key: 'sortDown',
        onClick: () =>
          setActiveSortingState([
            {
              id: columnId,
              desc: true,
            },
          ]),
      },
      {
        label: t('common:table.dropdown_clearSort'),
        iconName: 'ClearSort',
        key: 'clearSort',
        onClick: () => setActiveSortingState([]),
      },
    ];

    return items.filter(i => enabledKeys.includes(i.key));
  };

  const onRowClick = async (row: Row<CalendarEvents>) => {
    if (!isBasicOrLightUser && isEarningsEvent(row.original)) {
      const { id, entity_tag: entityTag } = row.original;
      const earningsInsightsData = await getEarningsInsightData(id);
      const earningsInsight = parseEarningsInsights(
        earningsInsightsData.data ?? []
      );

      if (earningsInsight) {
        openDrawer({
          entityTag,
          earningsId: id,
          subtab:
            earningsInsight.type === 'preview'
              ? SubTabsType.Preview
              : SubTabsType.Recap,
          source: POSTHOG_TRACKING_SOURCES.EVENTS,
        });
      } else {
        openCalendarEventDrawer(row);
      }
    } else {
      openCalendarEventDrawer(row);
    }
  };

  const openCalendarEventDrawer = (row: Row<CalendarEvents>) => {
    setQueryParams({
      [queryKeys.activeDrawer]: `${activeTab}-events`,
    });
    const eventType = getCalendarEventType(row.original);
    setDrawerData(row.original);
    trackEventsDrawerOpen('events', eventType);
  };

  const table = useReactTable<CalendarEvents>({
    data: activeDateData,
    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- columns are typed per-tab but data is CalendarEvents union
    columns: columns as ColumnDef<CalendarEvents>[],
    state: {
      sorting: activeSortingState,
    },
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  if (isError) {
    return <S.StyledBaseErrorView />;
  }

  if (!activeDateData?.length && !isLoading) {
    return <EmptyState activeTab={activeTab} />;
  }

  return (
    <>
      <S.TableWrapper ref={tableContainerRef} data-testid="events-table">
        <TanStackTableRoot>
          <thead>
            {table.getHeaderGroups().map(headerGroup => {
              return (
                <tr key={headerGroup.id}>
                  {headerGroup.headers.map((header, index) => {
                    const sortOrder = header.column.getIsSorted();
                    const alignment = getAlignment(index);
                    const dropdownItems = getDropdownItems(
                      header.column.id,
                      sortOrder
                    );
                    return (
                      <DropdownMenu
                        key={header.id}
                        triggerNode={
                          <S.StyledTableHeader
                            key={header.column.id}
                            isHoverEnabled={false}
                            alignment={alignment}
                          >
                            <TableHeaderIcons
                              isFiltered={header.column.getIsFiltered()}
                              sortDirection={sortOrder}
                              alignment={alignment}
                            >
                              {flexRender(
                                header.column.columnDef.header,
                                header.getContext()
                              )}
                            </TableHeaderIcons>
                          </S.StyledTableHeader>
                        }
                      >
                        <DropdownGroup items={dropdownItems} />
                      </DropdownMenu>
                    );
                  })}
                </tr>
              );
            })}
          </thead>
          <tbody>
            {isLoading || forceIsTableLoading ? (
              <EventTableBodySkeletonLoader
                columnLength={table.getHeaderGroups()[0].headers.length}
              />
            ) : (
              <>
                {table.getRowModel().rows.map(row => {
                  return (
                    <tr key={row.id} onClick={() => onRowClick(row)}>
                      {row.getVisibleCells().map((cell, index) => {
                        const isEntityCell = cell.column.id === 'entity';
                        return (
                          <S.StyledTableCell
                            key={cell.id}
                            alignment={getAlignment(index)}
                            condensed={isEntityCell}
                          >
                            {flexRender(
                              cell.column.columnDef.cell,
                              cell.getContext()
                            )}
                          </S.StyledTableCell>
                        );
                      })}
                    </tr>
                  );
                })}
                {hasNextPage && table.getRowModel().rows.length > 0 && (
                  <tr data-testid="next-page-loading-row">
                    {table
                      .getRowModel()
                      .rows[0].getVisibleCells()
                      .map(cell => (
                        <TableCell key={cell.id} isNumber={false}>
                          <SkeletonLoader
                            areas={[
                              {
                                styles: {
                                  width: '50%',
                                  height: '20px',
                                  borderRadius: '8px',
                                },
                              },
                            ]}
                          />
                        </TableCell>
                      ))}
                  </tr>
                )}
              </>
            )}
          </tbody>
        </TanStackTableRoot>
      </S.TableWrapper>
      {drawerData && (
        <div
          onClick={e => e.stopPropagation()}
          onKeyDown={e => e.stopPropagation()}
          role="button"
        >
          <CalendarEventDrawer
            closeDrawer={() => {
              setDrawerData(undefined);
              deleteQueryParam(queryKeys.activeDrawer);
            }}
            isOpen={!!drawerData}
            headerContent={getHeaderProps(drawerData)}
            drawerContent={getDrawerContent(drawerData)}
            onFullScreenClick={onFullScreenClick(drawerData)}
          />
        </div>
      )}
    </>
  );
};
